/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React, { useState, useMemo } from 'react';
import { Card, CardBody, Row, Col, Input } from 'reactstrap';
import { DataIntegrationConnector, useCreateConnectCardMutation, useListDataIntegrationConnectorsQuery } from '@api/initiative-data-integrations';
import { BasicAlert } from '@g17eco/molecules/alert';
import { addSiteAlert, addSiteError, SiteAlertColors } from '../../../../../slice/siteAlertsSlice';
import { isStaff } from '../../../../../selectors/user';
import { useAppSelector } from '../../../../../reducers';
import { generateUrl } from '../../../../../routes/util';
import { ROUTES } from '../../../../../constants/routes';
import G17EcoLogo from '../../../../../images/g17eco-icon.svg';
import { PreviewAlert } from '@g17eco/molecules/feature-stability/PreviewAlert';
import { BlockingLoader } from '@g17eco/atoms/loader';

interface AddDataConnectionTabProps {
  initiativeId: string;
  handleBack: () => void;
}

export const AddDataConnectionTab: React.FC<AddDataConnectionTabProps> = ({
  initiativeId,
  handleBack,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isConnectCardLoading, setIsConnectCardLoading] = useState(false);

  const [createConnectCardSession] = useCreateConnectCardMutation();
  const { data: connectors = [], isLoading: connectorsLoading } = useListDataIntegrationConnectorsQuery(
    { initiativeId }
  );

  const isStaffUser = useAppSelector(isStaff);

  const handleConnectorSelect = async (connectorId: string) => {
    try {
      setIsConnectCardLoading(true);
      const callbackUrl = connectorId === 'staff_snowflake' ?
        `${window.location.origin}${generateUrl(ROUTES.DATA_INTEGRATIONS, { initiativeId })}`
        : `${window.location.origin}${window.location.pathname}`;

      const { connect_card } = await createConnectCardSession({
        initiativeId,
        connectorId,
        redirectUri: callbackUrl,
      }).unwrap();

      // Open DataIntegration's ConnectCard UI in a popup
      const popup = window.open(connect_card.uri, 'data-integration-setup');

      // Listen for popup close
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnectCardLoading(false);
          handleBack(); // Switch to connections tab
          // Refresh connections list will happen automatically when tab switches
          addSiteAlert({ content: 'Data connection setup initiated in a new tab', color: SiteAlertColors.Success });
        }
      }, 1000);
    } catch (error) {
      setIsConnectCardLoading(false);
      addSiteError(error);
    }
  };

  const isLoading = connectorsLoading || isConnectCardLoading;

  // Filter connectors based on search term
  const filteredConnectors = useMemo(() => {
    if (isLoading) {
      return Array.from<DataIntegrationConnector>({ length: 8 }).map((c, i) => ({
          id: `loading-${i}`,
          name: 'Loading...',
          description: 'Loading connector details...',
          type: 'Database',
          icon: 'loading',
          icon_url: G17EcoLogo
        }));
    }

    const newConnectors = [...connectors];
    if (isStaffUser) {
      const snowflakeConnector = newConnectors.find((connector) => connector.id === 'snowflake_db');

      if (snowflakeConnector) {
        newConnectors.push({
          ...snowflakeConnector,
          id: 'staff_snowflake',
          name: `${snowflakeConnector.name} Demo`,
        });
      }
    }

    if (!searchTerm.trim()) {
      return newConnectors;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();

    return newConnectors.filter((connector) => {
      const matchesName = connector.name.toLowerCase().includes(lowerSearchTerm);
      const matchesDescription = connector.description?.toLowerCase().includes(lowerSearchTerm);
      const matchesType = connector.type.toLowerCase().includes(lowerSearchTerm);
      const matchesId = connector.id.toLowerCase().includes(lowerSearchTerm);

      return matchesName || matchesDescription || matchesType || matchesId;
    });
  }, [connectors, isStaffUser, searchTerm, isLoading]);

  // Calculate total available connectors including demo connector when applicable
  const totalAvailableConnectors = useMemo(() => {
    if (isLoading) {
      return 8; // Loading state shows 8 placeholder connectors
    }

    let total = connectors.length;
    if (isStaffUser) {
      const hasSnowflakeConnector = connectors.some((connector) => connector.id === 'snowflake_db');
      if (hasSnowflakeConnector) {
        total += 1; // Add 1 for the demo connector
      }
    }
    return total;
  }, [connectors, isStaffUser, isLoading]);


  return (
    <>
      <PreviewAlert />
      {isLoading ? <BlockingLoader /> : null }
      {/* Search Bar */}
      <div className='mb-4'>
        <Input
          type='text'
          placeholder='Search connectors...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='search-input'
        />
        <small className='text-muted'>
          Showing {filteredConnectors.length} of {totalAvailableConnectors} connectors
          {searchTerm && ` matching '${searchTerm}'`}
        </small>
      </div>

      {/* Connectors Grid */}
      {filteredConnectors.length === 0 ? (
        <BasicAlert type='info' >
          <i className='fal fa-search me-2' />
          {searchTerm ? `No connector types found matching '${searchTerm}'. Try a different search term.` : 'No connectors available.'}
        </BasicAlert>
      ) : (
        <Row>
          {filteredConnectors.map((connectorType) => (
            <Col md={6} lg={4} xl={3} key={connectorType.id} className='mb-3'>
              <Card
                className='h-100 connector-card'
                style={{ cursor: 'pointer' }}
                onClick={() => handleConnectorSelect(connectorType.id)}
              >
                <CardBody className='text-center'>
                  {connectorType.icon_url && (
                    <img
                      src={connectorType.icon_url}
                      alt={connectorType.name}
                      style={{ width: '48px', height: '48px', marginBottom: '12px' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <h6 className='mb-2'>{connectorType.name}</h6>
                  <p className='text-muted small mb-2'>{connectorType.description}</p>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </>
  );
};
